import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createScoreSubmission, getScoreSubmissions } from "../../actions/studensts";
import { LOGOUT } from "../../constants/actionTypes";
import { useDispatch } from "react-redux";
import Loading from "../../components/Loading";
import UNAUTHORIZED from "../UNAUTHORIZED";

const emptyForm = { national_id: "", full_name: "", act_id: "", amount: "", currency: "EGP" };

const STATUS_STYLES = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

const fmtDateTime = (val) => {
  if (!val) return "";
  const d = new Date(String(val).replace(" ", "T"));
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

export default function SendScoreForm() {
  const profile = JSON.parse(localStorage.getItem("profile"));
  const user = profile?.value?.[0];
  const role = user?.role;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    const res = await getScoreSubmissions();
    if (res?.status === 200) {
      setRows(res.data.data);
    } else {
      setError("Failed to load submissions. Please try again.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (role === "send_score") load();
  }, [load, role]);

  const handleChange = (e) => {
    setError("");
    setSuccess("");
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.national_id.trim() || !form.full_name.trim() || form.amount === "") {
      setError("Full Name, National ID and Amount are required.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    const res = await createScoreSubmission({
      national_id: form.national_id.trim(),
      full_name: form.full_name.trim(),
      act_id: form.act_id.trim(),
      amount: form.amount,
      currency: form.currency,
    });
    setSaving(false);
    if (res?.status === 201 || res?.status === 200) {
      setSuccess("Submitted successfully.");
      setForm(emptyForm);
      load();
    } else {
      setError(res?.data?.ERROR || "Submission failed. Please try again.");
    }
  };

  const handleLogout = () => {
    dispatch({ type: LOGOUT });
    navigate("/login");
  };

  // Only the send_score role may use this page — not even admin, so an
  // admin who navigates here directly is turned away just like anyone else.
  if (role !== "send_score") {
    return <UNAUTHORIZED />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-blue-900 text-white px-6 py-4 flex items-center justify-between">
        <h1 className="text-xl font-bold">Send Score Form</h1>
        <div className="flex items-center gap-4">
          <span className="text-blue-200 text-sm">{user?.username}</span>
          <button
            onClick={handleLogout}
            className="bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-3 py-1.5 rounded"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6">
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow p-6 mb-8"
        >
          <h2 className="text-lg font-semibold text-gray-800 mb-4">New Submission</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="full_name"
                value={form.full_name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg p-2 outline-blue-600"
                placeholder="Full Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                National ID Or Passport Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="national_id"
                value={form.national_id}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg p-2 outline-blue-600"
                placeholder="National ID or Passport Number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ACT ID
              </label>
              <input
                type="text"
                name="act_id"
                value={form.act_id}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg p-2 outline-blue-600"
                placeholder="ACT ID"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  name="amount"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg p-2 outline-blue-600"
                  placeholder="Amount"
                />
                <select
                  name="currency"
                  value={form.currency}
                  onChange={handleChange}
                  className="border border-gray-300 rounded-lg p-2 outline-blue-600 bg-white"
                >
                  <option value="EGP">EGP</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>
          </div>

          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
          {success && <p className="mt-4 text-sm text-green-700">{success}</p>}

          <div className="mt-5">
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded disabled:opacity-50"
            >
              {saving ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">My Submissions</h2>
            <span className="text-sm text-gray-500">{rows.length} total</span>
          </div>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loading />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-gray-500 text-center py-10">No submissions yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="text-left px-4 py-2">Full Name</th>
                    <th className="text-left px-4 py-2">National ID</th>
                    <th className="text-left px-4 py-2">ACT ID</th>
                    <th className="text-left px-4 py-2">Amount</th>
                    <th className="text-left px-4 py-2">Submitted At</th>
                    <th className="text-left px-4 py-2">Submitted By</th>
                    <th className="text-left px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t odd:bg-gray-50">
                      <td className="px-4 py-2">{r.full_name || "—"}</td>
                      <td className="px-4 py-2">{r.national_id}</td>
                      <td className="px-4 py-2">{r.act_id || "—"}</td>
                      <td className="px-4 py-2">{r.amount} {r.currency || "EGP"}</td>
                      <td className="px-4 py-2">{fmtDateTime(r.submitted_at)}</td>
                      <td className="px-4 py-2">{r.submitted_by || "—"}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            STATUS_STYLES[r.status] || "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
